import React from 'react'
import { connect } from 'redaction'
import moment from 'moment'
import actions from 'redux/actions'
import { compose, constants, links, mixpanel, snowplow, object } from 'common/helpers'

import injectGlobals from 'decorators/injectGlobals'

import Message from 'components/text/Message/Message'
import Button from 'components/buttons/Button/Button'
import WidthContainer from 'components/_new/layout/widthContainer/WidthContainer/WidthContainer'
import CloseIcon from 'components/ui/CloseIcon/CloseIcon'
import CommonButton from 'components/buttons/CommonButton/CommonButton'
import RedeemButton from 'components/buttons/RedeemButton/RedeemButton'
import ResubscribeButton from 'components/buttons/ResubscribeButton/ResubscribeButton'
import UnpauseButton from 'components/buttons/UnpauseButton/UnpauseButton'
import UnskipButton from 'components/buttons/UnskipButton/UnskipButton'
import OfferCountdownBanner from 'components/banners/OfferCountdownBanner/OfferCountdownBanner'

import CountdownBanner from './CountdownBanner/CountdownBanner'
import UpdateToPayPalButton from './UpdateToPayPalButton/UpdateToPayPalButton'

import messages from './messages'
import s from './Banner.scss'


const inviteBannerName = 'insideLayoutInviteBanner'

const SimpleButton = (props) => (
  <CommonButton className={s.button} sm {...props} />
)

const SubscribeNowButton = (props) => (
  <SimpleButton
    subscribeNow
    dataTestId="subscribeNowButton"
    onClick={() => {
      snowplow.track(constants.snowplowEvents.subscribeNowLinkClick, {
        placement: 'header banner',
      })
    }}
    {...props}
  />
)

const Banner = (props) => {
  const {
    isLoggedIn, gender, cartProducts,
    subscription: { isUndeliverable, isCancelled, isNotSubscribed, isPaused, isUnpaid, pauseDate, isOnHold },
    isCountdownVisible, isPayPalVisible, isInviteVisible, isRedeemVisible, isTossInPage,
    isProductPageLandingVersionEnabled, isUnskipFlowEnabled, isOfferCountdownBannerVisible,
    isImproveSurveyVisible, isMobile, isFragranceConceptsVisible, isRecommendationsImproveVisible,
  } = props

  if (isTossInPage || isProductPageLandingVersionEnabled) {
    return null
  }

  if (isCountdownVisible) {
    return (
      <CountdownBanner />
    )
  }

  if (isOfferCountdownBannerVisible) {
    return (
      <OfferCountdownBanner />
    )
  }

  const isVisible = Boolean(
    !isLoggedIn || isUndeliverable || isNotSubscribed || isCancelled || isPayPalVisible || isInviteVisible || isPaused || isUnpaid
  )

  if (!isVisible) {
    return null
  }

  let message = messages.getAccess
  let closeButton
  let button
  let lowerMessageOnMobile

  // TODO add loader button

  if (!isLoggedIn) {
    button = (
      <SubscribeNowButton to={links.register} />
    )
  }
  else if (isUndeliverable) {
    message = {
      ...messages.undeliverable,
      values: {
        date: moment(pauseDate)
          .format('DD MMM YYYY'),
      },
    }

    button = (
      <SimpleButton
        className={s.button}
        to={links.profile.billingAndShipping}
        update
        dataTestId="updateShippingAddressButton"
      />
    )
  }
  else if (isUnpaid) {
    message = messages.paymentProblem

    button = (
      <SimpleButton
        to={links.cardUpdate}
        update
        dataTestId="updateBillingInfoButton"
        onClick={() => snowplow.track(constants.snowplowEvents.cardUpdateBannerClick)}
      />
    )
  }
  else if (isRedeemVisible) {
    message = messages.redeemText

    button = (
      <RedeemButton size="sm" color="whiteColorBrand" weight="medium" />
    )
  }
  else if (isRecommendationsImproveVisible) {
    message = messages.improveRecommendations

    closeButton = (
      <CloseIcon
        className={s.closeButton}
        whiteColor
        dataTestId="closeRecommendationsImproveBannerButton"
        onClick={() => actions.models.ui.hideBanner(constants.cookieNames.banners.recommendationsImprove)}
      />
    )

    button = (
      <Button
        className={s.button}
        sm
        title={messages.improve}
        to={links.improveRecommendations}
        dataTestId="improveButton"
        onClick={() => mixpanel.track(constants.analyticsEvents.improveRecommendations.start, {
          placement: 'top banner',
        })}
      />
    )
  }
  else if (isFragranceConceptsVisible) {
    message = messages.fragranceConcepts

    closeButton = (
      <CloseIcon
        className={s.closeButton}
        whiteColor
        dataTestId="closeFragranceConceptsBannerButton"
        onClick={() => actions.models.ui.hideFragranceConceptsBanner()}
      />
    )

    button = (
      <SimpleButton
        imIn
        to={links.incubator.fragranceConcepts}
        dataTestId="fragranceConceptsButton"
        onClick={() => {
          mixpanel.track(constants.analyticsEvents.concept.banner)
        }}
      />
    )
  }
  else if (isImproveSurveyVisible) {
    message = messages.helpUsToImprove
    lowerMessageOnMobile = messages.loveToHear

    closeButton = (
      <CloseIcon
        className={s.closeButton}
        whiteColor
        dataTestId="closeSurveyBannerButton"
        onClick={() => actions.models.ui.hideImproveSurveyBanner()}
      />
    )

    button = (
      <SimpleButton
        imIn
        to={links.improveSurvey}
        dataTestId="improveSurveyButton"
        onClick={() => {
          mixpanel.track(constants.analyticsEvents.surveyForm.banner)
        }}
      />
    )
  }
  else if (isNotSubscribed) {
    button = (
      <SubscribeNowButton to={cartProducts.ids.length ? links.checkout : links.subscribe} />
    )
  }
  else if (isCancelled) {
    button = (
      <ResubscribeButton size="sm" color="whiteColorBrand" placement="header banner" />
    )
  }
  else if (isPaused) {
    button = (
      <UnpauseButton size="sm" color="whiteColorBrand" placement="header banner" />
    )
  }
  else if (isOnHold && isUnskipFlowEnabled) {
    button = (
      <UnskipButton size="sm" color="whiteColorBrand" placement="header banner" />
    )
  }
  else if (isPayPalVisible) {
    message = messages.upgradeToPayPal

    closeButton = (
      <CloseIcon
        className={s.closeButton}
        whiteColor
        dataTestId="closeUpdateToPayPalBanner"
        onClick={() => actions.models.ui.hidePayPalBanner()}
      />
    )

    button = (
      <UpdateToPayPalButton className={s.button} sm />
    )
  }
  else if (isInviteVisible) {
    message = (gender === 'male' ? messages.freeCologne : messages.freePerfume)

    closeButton = (
      <CloseIcon
        className={s.closeButton}
        whiteColor
        dataTestId="closeInviteBanner"
        onClick={() => actions.models.ui.setClosedPopup(inviteBannerName)}
      />
    )

    button = (
      <Button
        className={s.button}
        sm
        title={messages.getAFreeScent}
        to={links.invite}
        dataTestId="inviteButton"
        onClick={() => mixpanel.track(constants.analyticsEvents.shareLinkClick, {
          placement: 'top banner',
        })}
      />
    )
  }

  const withLowerMessage = Boolean(lowerMessageOnMobile)

  return (
    <div
      className={s.banner}
      role="complementary"
      id="aboveHeaderBanner"
      aria-labelledby="headerBannerText headerBannerButton"
      data-testid={constants.testIds.banners.aboveHeaderBanner}
    >
      {closeButton}
      <WidthContainer>
        <div className={s.row}>
          <div className={s.col} id="headerBannerText">
            {
              withLowerMessage && !isMobile ? (
                <div className={s.text}>
                  <Message text={message} html />&nbsp;
                  <Message text={lowerMessageOnMobile} />
                </div>
              ) : (
                <Message className={s.text} tag="div" text={message} html />
              )
            }
          </div>
          <div className={s.col} id="headerBannerButton">{button}</div>
          {
            withLowerMessage && isMobile && (
              <div className={s.col}>
                <Message className={s.lowerText} tag="div" text={lowerMessageOnMobile} />
              </div>
            )
          }
        </div>
      </WidthContainer>
    </div>
  )
}


export default compose(
  injectGlobals(({ getCookieValue, getFeatureToggle, globals: { device: { isMobile } } }) => ({
    isPayPalBannerEnabled: getFeatureToggle(constants.features.payPalSwitchBanner),
    isUnskipFlowEnabled: getFeatureToggle(constants.features.unskipFlow),
    isFirstTimeVisitModalEnabled: getFeatureToggle(constants.features.firstTimeVisitModalWithTimer),
    isBillingDay1stEnabled: getFeatureToggle(constants.features.billingDay1stDayOfTheMonth),
    isImproveSurveyEnabled: getFeatureToggle(constants.features.improveSurvey),
    isRecommendationsImproveEnabled: getFeatureToggle(constants.features.recommendationsImprove),
    isImproveSurveyBannerHiddenCookie: getCookieValue(constants.cookieNames.isImproveSurveyBannerHidden),
    isFragranceConceptsEnabled: getFeatureToggle(constants.features.fragranceConcepts),
    isFragranceConceptsBannerHiddenCookie: getCookieValue(constants.cookieNames.isFragranceConceptsBannerHidden),
    isRecommendationsImproveBannerCookie: getCookieValue(constants.cookieNames.banners.recommendationsImprove),
    isMobile,
  })),
  connect({
    gender: 'models.me.gender',
    isLoggedIn: 'models.me.isLoggedIn',
    subscription: 'models.subscription',
    cartProducts: 'models.cart.items.products',
    isCountdownVisible: (state, { isBillingDay1stEnabled }) => {
      const {
        models: {
          subscription: {
            isActive: isSubscriptionActive, subscriptionDate,
            isOnHold: isSubscriptionOnHold, isResubscribed,
            unskipDate,
          },
          me: { stats: { queue: queueLength } },
          ui: { closedPopups: { countdownBanner: isVisible } },
        },
        routing: { location: { pathname } },
      } = state

      const subDate = subscriptionDate ? new Date(subscriptionDate) : 0
      const unskipedDate = unskipDate ? new Date(unskipDate) : 0

      const firstDayDelimiter = isBillingDay1stEnabled ? 1  : 5
      const lastDayDelimiter  = isBillingDay1stEnabled ? 18 : 22

      // show Countdown after unskip made between 5th and 22th of the month only
      // https://scentbird.atlassian.net/browse/SCNT-3711
      const isUnskipFlow = (
        unskipedDate > subDate && unskipedDate.getDate() > firstDayDelimiter
        && unskipedDate.getDate() < lastDayDelimiter
      )

      if ((isVisible || isResubscribed) && !pathname.includes('thankyou') && !isUnskipFlow) {
        return false
      }

      const isEmptyQueue = queueLength === 0
      const startDate = isUnskipFlow ? unskipedDate : subDate
      const activeTime = 60 * 60 * 12 * 1000
      const isTimeout = Date.now() - startDate > activeTime

      return (isSubscriptionActive || isSubscriptionOnHold) && isEmptyQueue && !isTimeout
    },
    isPayPalVisible: (state, props) => {
      const {
        models: {
          ui: { isPayPalBannerVisible },
          subscription: { isCancelable, isCancelled },
          payment: { paymentOption },
        },
      } = state
      const { isPayPalBannerEnabled } = props

      const isCreditCardPaymentMethod = paymentOption === 'creditCard'
      const isSubscriptionOnCancellingProcess = !isCancelable && !isCancelled

      return (
        isPayPalBannerEnabled
        && isPayPalBannerVisible
        && isCreditCardPaymentMethod
        && !isSubscriptionOnCancellingProcess
      )
    },
    isInviteVisible: (state, props) => {
      const {
        models: { ui: { closedPopups: { [inviteBannerName]: isInviteBannerClosed } }, subscription: { isSubscribed } },
        routing: { location: { pathname } },
      } = state

      return (
        !isInviteBannerClosed
        && pathname !== links.invite
        && isSubscribed
      )
    },
    isImproveSurveyVisible: (state, props) => {
      const {
        models: { ui: { isImproveSurveyBannerHidden }  },
      } = state
      const { isImproveSurveyEnabled, isImproveSurveyBannerHiddenCookie } = props

      return (
        isImproveSurveyEnabled
        && !isImproveSurveyBannerHidden
        && !isImproveSurveyBannerHiddenCookie
      )
    },
    isFragranceConceptsVisible: (state, props) => {
      const { models: { ui: { isFragranceConceptsBannerHidden } } } = state
      const { isFragranceConceptsEnabled, isFragranceConceptsBannerHiddenCookie } = props

      if (isFragranceConceptsEnabled && isFragranceConceptsBannerHidden) {
        return false
      }

      return isFragranceConceptsEnabled && !isFragranceConceptsBannerHiddenCookie
    },
    isRecommendationsImproveVisible: (state, props) => {
      const isRecommendationsImproveHidden = object.get(state, 'models.ui.banners.RecommendationsImproveBanner')
      const { isRecommendationsImproveEnabled, isRecommendationsImproveBannerCookie } = props

      if (isRecommendationsImproveEnabled && isRecommendationsImproveHidden) {
        return false
      }

      return isRecommendationsImproveEnabled && !isRecommendationsImproveBannerCookie
    },
    isRedeemVisible: (state) => {
      const {
        models: {
          subscription: { isSubscribed },
          me: { hasMarketingCoupon, giftSubscription },
        },
      } = state

      return Boolean(
        !isSubscribed
        && (
          hasMarketingCoupon
          || (giftSubscription && giftSubscription.code && giftSubscription.activated === false)
        ),
      )
    },
    isTossInPage: (state) => {
      const { routing: { location: { pathname } } } = state

      return pathname === links.tossIn
    },
    isProductPageLandingVersionEnabled: 'view.productPage.isLandingVersionEnabled',
    isOfferCountdownBannerVisible: (state, props) => {
      const {
        models: {
          subscription: { isNotSubscribed },
          me: { isLoggedIn },
          ui: {
            closedPopups: {
              [constants.modals.FirstTimeVisit]: firstTimeModalData,
              [constants.popups.notSubscribedCountdown]: countdownData,
            },
          },
        },
      } = state
      const { isFirstTimeVisitModalEnabled } = props

      /*
        https://scentbird.atlassian.net/browse/SCNT-5276

        we show this banner if
          - countdown modal was closed
          - countdown is not finished or total countdown visibility time is less than one hour

        'notSubscribedCountdown' data contains lastUpdate which equals the start of timer
        and count:
          - if count = 1 then it's visible
          - if count >= 2 then we hide it
      */

      const oneHour = 1000 * 60 * 60

      return isFirstTimeVisitModalEnabled
        && isLoggedIn
        && isNotSubscribed
        && firstTimeModalData
        && firstTimeModalData.count
        && countdownData
        && countdownData.count === 1
        // we should display the banner for one hour
        && Date.now() - new Date(countdownData.lastUpdate).getTime() <= oneHour
    },
  }),
)(Banner)
